/**
 * Module: `export/run-response-export.ts` — orchestrates build + render + download.
 */

import { buildResponseExportDocument } from "@/lib/export/build-response-export-document";
import { downloadBlob } from "@/lib/export/download";
import {
  buildExportFilename,
  findQuestionNumberForAssistantMessage,
} from "@/lib/export/export-filename";
import { renderExportToDocx } from "@/lib/export/render-docx";
import { renderExportToPdf } from "@/lib/export/render-pdf";
import type { ResponseExportFormat } from "@/lib/export/response-export-document";
import { findUserQuestionForAssistantMessage } from "@/lib/messages/find-user-question-for-assistant";
import type { Message, SidoniSource, Source } from "@/types";

export interface RunResponseExportInput {
  assistantMessage: Message;
  questionText: string | null | undefined;
  format: ResponseExportFormat;
  enrichedSources?: Array<Source | SidoniSource>;
  /** Full transcript — used for Q{n} and question date. */
  messages?: Message[];
}

export async function runResponseExport({
  assistantMessage,
  questionText,
  format,
  enrichedSources,
  messages = [],
}: RunResponseExportInput): Promise<void> {
  const doc = buildResponseExportDocument({
    assistantMessage,
    questionText,
    enrichedSources,
  });

  const blob =
    format === "pdf"
      ? await renderExportToPdf(doc)
      : await renderExportToDocx(doc);

  const questionMessage = findUserQuestionForAssistantMessage(
    messages,
    assistantMessage.id
  );
  const questionNumber = findQuestionNumberForAssistantMessage(
    messages,
    assistantMessage.id
  );

  downloadBlob(
    blob,
    buildExportFilename({
      format,
      questionNumber,
      questionText: questionText ?? questionMessage?.content ?? null,
      questionDate: questionMessage?.timestamp ?? null,
      answerDate: assistantMessage.timestamp ?? null,
      exportedAt: new Date(doc.exportedAt),
    })
  );
}
